/**
 * Base component for chatbot selection
 * Reduces duplication between chatbot-interaction and conversation-viewer
 */
import { Component, OnInit, OnDestroy, signal, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChatbotInteractionService } from '../../../pages/chatbot-interaction/services/chatbot-interaction.service';
import { Chatbot } from '../../../core/models/chatbot.model';

@Component({
  template: '', // Abstract component, template in child components
  standalone: true,
})
export abstract class ChatbotSelectorBaseComponent implements OnInit, OnDestroy {
  protected readonly destroy$ = new Subject<void>();
  @Input() redirectPath: string = '/chatbot'; // Default path, can be overridden
  
  searchQuery = '';
  chatbots = signal<Chatbot[]>([]);
  loading = signal<boolean>(true);
  error = signal<string | null>(null);

  constructor(
    protected router: Router,
    protected chatbotService: ChatbotInteractionService
  ) {}

  ngOnInit(): void {
    this.loadChatbots();
  }

  loadChatbots(): void {
    this.loading.set(true);
    this.error.set(null);

    this.chatbotService.getChatbotsByUser().pipe(takeUntil(this.destroy$)).subscribe({
      next: (chatbots) => {
        this.chatbots.set(chatbots);
        this.loading.set(false);
      },
      error: (error) => {
        this.error.set('Error al cargar los chatbots');
        this.loading.set(false);
      },
    });
  }

  get filteredChatbots(): Chatbot[] {
    if (!this.searchQuery.trim()) {
      return this.chatbots();
    }

    const query = this.searchQuery.toLowerCase();
    return this.chatbots().filter(
      (bot) =>
        bot.name.toLowerCase().includes(query) ||
        bot.description.toLowerCase().includes(query) ||
        bot.code.toLowerCase().includes(query)
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectChatbot(chatbotId: number): void {
    const bot = this.chatbots().find((b) => b.chatbotId === chatbotId);
    if (bot) {
      this.router.navigate([this.redirectPath, bot.code], {
        state: { chatbot: bot }
      });
    }
  }
}
